import { useState, useEffect, useRef } from 'react';
import { getCardBankName, InnocodePaymentCardInfo } from '@intsoft/innocode-payment';
import { InnocodeButton, InnocodeInput, InnocodeTextButton, useDialog, useModal } from '@intsoft/innocode-core';
import { PasswordChangeModal } from '../../../resources/modal/PasswordChangeModal';
import { MainCardSelectModal } from '../../../resources/modal/MainCardSelectModal';
import { RegisterCardModal } from '../../../resources/modal/RegisterCardModal';
import styles from './Payment.module.css';
import { IAM_PATH, IAM_SERVER_TENANT_USER, ZENPAY_BILLING, ZENPAY_PAY_PASSWORD } from '../../../utils/Config';
import { getCookie } from '../../../utils/cookies/cookies';
import axios from "../../../utils/api/axios";
import { BankKRType, BankType, CardType, FactorNameType, mapApiBrandToCardType, mapApiIssuerToBankType } from '../../../resources/utils/bank-types';
import { usePasswordChangeModal } from '../../NavBar/component/usePasswordChangeModal';
import { useNavigate } from 'react-router-dom';

interface ApiCardData {
  id: number;
  cardNumber: string;
  brand: string;      // ex: "마스터", "비자"
  issuer: string;     // ex: "KB국민카드"
  statementFactorName?: string; // ex: "카카오"
  userId: string;
  cardType: string;
  cardName: string;
  billingKey: string;
}

export interface CardInfo {
  id: string;
  cardNumber?: string;
  cardType: string;
  cardBrand: string;
  cardBankType?: string;
  cardBrandName?: string;
  cardName?: string;
  billingKey?: string;
  status: "active" | "inactive";
}
export default function Page() {
  const [platform, setPlatform] = useState<'web' | 'tablet-vertical' | 'mobile'>('web');
  const { showInnocodeModal: showBottomModal } = useModal();
  const [enableEditMode, setEnableEditMode] = useState(false);
  const { showDialog } = useDialog();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const isRegisterProcessing = useRef(false);


  const { handleOpenModal, isLoading } = usePasswordChangeModal();

  const toggleEditMode = () => {
    setEnableEditMode(!enableEditMode);
  };

  const getPlatform = (width: number): 'web' | 'tablet-vertical' | 'mobile' => {
    if (width < 768) return 'mobile';
    if (width < 1440) return 'tablet-vertical';
    return 'web';
  };

  const [cardList, setCardList] = useState<CardInfo[]>([]);


  useEffect(() => {
    // 초기 사이즈 설정 
    setPlatform(getPlatform(window.innerWidth));
    // 윈도우 리사이즈 이벤트 핸들러
    const handleResize = () => {
      setPlatform(getPlatform(window.innerWidth));
    };

    window.addEventListener('resize', handleResize);

    // 클린업 함수
    return () => {
      window.removeEventListener('resize', handleResize);
    };


  }, []);

  useEffect(() => {
    getBillingCard();
  }, []);

  const getBillingCard = async () => {
    setLoading(true);
    try {
      const url = ZENPAY_BILLING + "/all";
      const conditions = [
        { field: "userId", operator: "EQUALS", value: getCookie("user_id") }
      ];
      const params = {
        page: 0,
        size: 0,
        sortField: ['createdDate'],
        sortOrder: ['asc'],
        operator: "AND",
        conditions
      };

      const res = await axios.post(url, params);
      const favoriteCard = await axios.get(`${ZENPAY_BILLING}/get/favorite-card`, { params: { userId: getCookie("user_id") } });

      const { data, recordsTotal } = res.data.data;
      const favoriteCardData = favoriteCard.data.data;
      const apiCardDataList: ApiCardData[] = data;
      console.log(apiCardDataList);


      // API 데이터를 CardInfo[] 형태로 변환
      const transformedCardList: CardInfo[] = apiCardDataList.map((apiCard) => {

        const cardBankType = mapApiIssuerToBankType(apiCard.issuer, apiCard.statementFactorName);
        const cardBrand = mapApiBrandToCardType(apiCard.brand);

        const cardName = getCardBankName(cardBankType); // ex: "신한카드"
        // 제공된 라이브러리 헬퍼 함수들을 사용
        const status = apiCard.billingKey === favoriteCardData.billingKeyId ? 'active' : 'inactive';


        return {
          id: String(apiCard.id),
          cardNumber: apiCard.cardNumber,
          cardType: apiCard.cardType == "체크" ? "Check" : "",
          cardBrand: cardBrand,
          cardBankType: cardBankType,
          cardName: apiCard.cardName || '기타 카드', // getCardBankName이 빈 문자열을 반환할 경우 대비
          cardBrandName: `${cardName || '기타'} ${cardBrand === CardType.DEFAULT ? '카드' : cardBrand}`, // 예시 로직
          billingKey: apiCard.billingKey,
          status: status,
        };
      });

      console.log(transformedCardList);

      setCardList(transformedCardList);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };


  const handleDeleteCard = (currentCard: CardInfo) => {
    showDialog({
      type: 'delete',
      iconType: 'warning',
      title: '등록한 카드를\n삭제하시겠어요?',
      cancelText: '아니요',
      confirmText: '예',
      onConfirm: () => {

        if (isLoading) return;
        handleOpenModal({
          selectMode: 'verify',
          onSuccess: () => {
            showDialog({
              type: 'info',
              iconType: 'check',
              title: '선택하신 카드를 삭제했습니다.',
              confirmText: '완료',
              onConfirm: async () => {

                const params = {
                  userId: getCookie("user_id"),
                  billingKey: currentCard.billingKey,
                };

                const res = await axios.post(`${ZENPAY_BILLING}/delete`, params);

                if (res.data.code === 200) {
                  setCardList(cardList.filter((card: CardInfo) => card.id !== currentCard.id));
                }
              }
            })
          }
        });
      }
    });
  };

  const EditTitleForm: React.FC<{
    initialTitle: string;
    onSave: (title: string) => void;
    onClose: () => void;
  }> = ({ initialTitle, onSave, onClose }) => {
    const [localTitle, setLocalTitle] = useState<string>(initialTitle);

    return (
      <div style={{ width: '100%', gap: '24px', display: 'flex', flexDirection: 'column' }}>
        <InnocodeInput
          value={localTitle || ''}
          maxLength={10}
          onChange={(e: any) => {
            const value = e.target.value;
            if (value.length <= 10) {
              setLocalTitle(value);
            }
          }}
          showTrailingContent={true}
          trailingContent={
            <span>{localTitle?.length || 0}/10</span>
          }
          autoFocus
        />
        <div style={{ display: 'flex', gap: '8px' }}>
          <InnocodeButton
            theme="line" designType="assistive" size="lg" style={{ width: '100%' }}
            onClick={() => {
              onClose();
            }}
          >
            취소
          </InnocodeButton>
          <InnocodeButton
            theme="solid" designType="primary" size="lg" style={{ width: '100%' }}
            onClick={() => {
              if (localTitle.trim() !== '') {
                onSave(localTitle);
              }
            }}
          >
            변경
          </InnocodeButton>
        </div>
      </div>
    );
  };

  const handleModifyCard = (currentCard: CardInfo) => {
    showDialog({
      title: '템플릿 제목',
      message: '수정할 템플릿 제목을 입력해주세요.',
      enableActionFields: false,
      customContent: (closeDialog) => (
        <EditTitleForm
          initialTitle={currentCard.cardName || ''}
          onSave={async (value) => {
            closeDialog();
            const params = {
              userId: getCookie("user_id"),
              cardName: value,
              billingKey: currentCard.billingKey,
            };

            const res = await axios.post(`${ZENPAY_BILLING}/update/billingName`, params);
            if (res.data.code === 200) {
              showDialog({
                type: 'info',
                iconType: 'check',
                title: '카드명 변경이\n완료되었습니다.',
                confirmText: '완료',
                onConfirm: () => {
                  setCardList(cardList.map((card: CardInfo) => currentCard.id === card.id ? { ...card, cardName: value } : card));
                }
              })
            } else {
              showDialog({
                type: 'error',
                iconType: 'warning',
                title: '카드명 변경이\n완료되지 않았습니다.\n다시 시도해주세요.',
                confirmText: '오류',
                onConfirm: () => {
                }
              })
            }



          }}
          onClose={closeDialog}
        />
      )
    });
  }

  const handleMainCardSelect = () => {
    if (cardList.length === 0) {
      showDialog({
        type: 'info',
        iconType: 'check',
        title: '등록된 카드가 없습니다.',
        confirmText: '확인',
        onConfirm: () => {
        }
      })
      return;
    }



    showBottomModal({
      enableDefaultStyle: true,
      headerBuilder: () => { return <></>; },
      contentBuilder: (
        _data: any,
        _handleSave: any,
        _handleDelete: any,
        _handleUpdate: any,
        handleClose: any,
        _modalType: any
      ) => {
        return <MainCardSelectModal
          isOpen={true}
          onClose={handleClose}
          cardList={cardList}
          onConfirm={(selectedCard: CardInfo) => {
            handleClose();
            showDialog({
              type: 'confirm',
              iconType: 'info',
              title: '선택하신 카드를 대표 카드로\n등록하시겠어요?',
              confirmText: '예',
              cancelText: '아니요',
              onConfirm: async () => {

                const params = {
                  userId: getCookie("user_id"),
                  billingKeyId: selectedCard.billingKey,
                };

                const res = await axios.post(`${ZENPAY_BILLING}/update/favorite-card`, params);

                if (res.data.code === 200) {
                  showDialog({
                    type: 'info',
                    iconType: 'check',
                    title: '대표 카드로\n등록했습니다.',
                    confirmText: '완료',
                    onConfirm: () => {
                      getBillingCard();
                    }
                  })
                } else {
                  showDialog({
                    type: 'error',
                    iconType: 'warning',
                    title: '대표 카드로\n등록하지 못했습니다.\n다시 시도해주세요.',
                    confirmText: '오류',
                    onConfirm: () => {

                    }
                  })
                }
              }
            })
          }}
        />;
      },
    });
  }

  const handleRegisterCardSelect = async () => {
    if (isRegisterProcessing.current) return;
    isRegisterProcessing.current = true;

    try {
      const res = await axios.get(`${IAM_PATH}/status/authinfo/${getCookie("user_id")}`);
      console.log("두번 클릭됨?");
      if (res.data.data === true) {
        showBottomModal({
          enableDefaultStyle: true,
          headerBuilder: () => { return <></>; },
          contentBuilder: (
            _data: any,
            _handleSave: any,
            _handleDelete: any,
            _handleUpdate: any,
            handleClose: any,
            _modalType: any
          ) => {
            return <RegisterCardModal
              isOpen={true}
              onClose={handleClose}
              onConfirm={async (data: any) => {
                handleClose();
                if (data.code === 401) {
                  showDialog({
                    type: 'info',
                    iconType: 'warning',
                    title: '이미 등록된 카드입니다.',
                    confirmText: '확인',
                    onConfirm: () => { }
                  })
                  return;
                } else if (data.code === 502) {
                  showDialog({
                    type: 'info',
                    iconType: 'warning',
                    title: '카드 정보가 일치하지 않습니다.',
                    confirmText: '확인',
                    onConfirm: () => { }
                  })
                  return;
                } else if (data.code === 200) {
                  const response = await axios.get(`${ZENPAY_PAY_PASSWORD}/status/${getCookie('user_id')}`);
                  const notHavePaymentPassword = response.data.data;
                  // 페이 비밀번호 체크
                  if (!notHavePaymentPassword) {
                    showDialog({
                      type: 'info',
                      iconType: 'info',
                      title: '페이 비밀번호를\n생성해주세요.',
                      message: '페이 비밀번호를 생성 후\n젠트리페이 결제를 이용하실 수 있습니다.',
                      confirmText: '페이 비밀번호 설정',
                      onConfirm: () => {

                        handleOpenModal({
                          selectMode: 'create',
                          onSuccess: () => { getBillingCard(); }
                        });
                      }
                    })
                  } else {
                    showDialog({
                      type: 'info',
                      iconType: 'check',
                      title: '카드 등록이\n완료되었습니다.',
                      confirmText: '완료',
                      onConfirm: () => { getBillingCard(); }
                    })
                  }
                } else {
                  showDialog({
                    type: 'info',
                    iconType: 'warning',
                    title: '카드 등록에 실패했습니다.',
                    confirmText: '확인',
                    onConfirm: () => { }
                  })
                }
              }}
            />;
          },
        });
      } else {
        showDialog({
          type: 'info',
          iconType: 'check',
          title: '본인 인증을 먼저 진행해주세요.',
          confirmText: '본인 인증',
          onConfirm: () => {
            //본인인증페이지로 이동
            navigate("/account/manage");
          }
        })
      }
    } finally {
      isRegisterProcessing.current = false;
    }
  }



  return (
    <div className={styles.layoutWrapper}>
      <div className={styles.cardListTitle}>
        <h3>결제 관리</h3>
        {platform === "web" && (
          <InnocodeButton theme="line" designType="primary" size="sm" onClick={handleMainCardSelect}>
            대표카드 등록
          </InnocodeButton>
        )}
        {platform !== 'web' ? (enableEditMode ? (
          <InnocodeTextButton size="sm" type="primary" onClick={toggleEditMode}><p>완료</p></InnocodeTextButton>
        ) : (
          <InnocodeTextButton size="sm" type="primary" onClick={toggleEditMode}><p>편집</p></InnocodeTextButton>
        )) : null}
      </div>
      <div className={styles.cardListWrapper}>
        {cardList.map((card: CardInfo) => (
          <InnocodePaymentCardInfo
            className="card-item"
            key={card.id}
            cardType={'card'}
            cardBankName={`${card.cardBrandName} 123 `}
            cardName={card.cardName}
            cardNumber={`${card.cardNumber} ${card.cardType}`}
            cardBankType={`${card.cardBankType}`}
            size={platform === 'mobile' ? 'sm' : (platform === 'tablet-vertical') ? 'md' : 'lg'}
            type="card"
            showBadge={card.status === "active"}
            badge={{ children: "대표 카드" }}
            showModifyButton={platform === 'web' ? true : enableEditMode}
            onModify={() => { handleModifyCard(card) }} showDeleteButton={platform === 'web' ? true : enableEditMode} onDelete={() => { handleDeleteCard(card) }} />
        ))}
        <InnocodePaymentCardInfo
          className="card-item"
          cardType={'register'}
          size={platform === 'mobile' ? 'sm' : (platform === 'tablet-vertical') ? 'md' : 'lg'}
          type='register'
          cardName={"새 카드 등록"}
          showBadge={false}
          showDeleteButton={false}
          onClick={handleRegisterCardSelect}
        /> 
      </div>
    </div>
  );
}